import { Flag, Star } from 'lucide-react'
import { cn } from '@/lib/cn'
import type { MarkKind } from '@/types'

export type StatusFilter = 'pending' | 'approved' | 'rejected' | 'favorites' | 'flagged'

export type StatusCounts = Record<StatusFilter, number>

const VERDICT_TABS: Array<{ value: StatusFilter; label: string }> = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
]

/** The fourth tab follows the page's marker: programs flag, everything else stars. */
const MARK_TABS: Record<MarkKind, { value: StatusFilter; label: string }> = {
  favorite: { value: 'favorites', label: 'Favorites' },
  flagged: { value: 'flagged', label: 'Flagged' },
}

/**
 * Counts are computed over the unfiltered set so the numbers do not move while
 * you type in the search box (docs/06-component-refactor.md §4.1).
 */
export function StatusTabs({
  value,
  counts,
  onChange,
  mark = 'favorite',
  className,
}: {
  value: StatusFilter
  counts: StatusCounts
  onChange: (next: StatusFilter) => void
  mark?: MarkKind
  className?: string
}) {
  const tabs = [...VERDICT_TABS, MARK_TABS[mark]]
  return (
    <div
      role="tablist"
      aria-label="Filter by review status"
      className={cn('flex gap-1 border-b border-rule', className)}
    >
      {tabs.map((tab) => {
        const active = value === tab.value
        return (
          <button
            key={tab.value}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(tab.value)}
            className={cn(
              'relative -mb-px flex items-center gap-1.5 border-b-2 px-2.5 py-2',
              'font-head text-[13px] font-semibold transition-colors',
              active
                ? 'border-brand-blue text-brand-blue-800'
                : 'border-transparent text-ink-muted hover:text-ink',
            )}
          >
            {tab.value === 'favorites' && (
              <Star size={12} fill={active ? 'currentColor' : 'none'} aria-hidden />
            )}
            {tab.value === 'flagged' && (
              <Flag size={12} fill={active ? 'currentColor' : 'none'} aria-hidden />
            )}
            {tab.label}
            <span className="tabular text-[12px] font-normal text-ink-faint">
              {counts[tab.value]}
            </span>
          </button>
        )
      })}
    </div>
  )
}
